/**
 * THE REAL SFU — a `MediaClient` backed by livekit-client 2.21.
 *
 * Responsibilities are split so this file is wiring and nothing else:
 *   localMedia + livekitLocalControls  capture and publication
 *   livekitCodec / livekitRoom         AV1→VP9→VP8, the EX44 simulcast ladder
 *   livekitSubscriptions               the density policy, on the wire
 *   livekitEvents                      RoomEvent → the contract's event surface
 *   livekitStats                       real getStats() → the ladder
 *   mediaStore                         the snapshot the UI subscribes to
 *
 * Density decisions are NOT taken here. They arrive as a `SubscriptionPlan`
 * from `tilePlan.ts`; this client's only job is to make the wire match it.
 */
import type { QualityProfile } from '@cyphex/types';
import { Track, type Room } from 'livekit-client';
import type {
  ConnectOptions, MediaClient, MediaParticipant, RecordingState, ShareStopReason,
  SubscriptionPlan, VideoSource,
} from './contract';
import { createAttachmentRegistry } from './livekitAttachments';
import { createAudioSink, watchAudioPlayback, type AudioSink } from './livekitAudio';
import { bindRoomEvents, createRoomEventHost } from './livekitEvents';
import { createLocalControls, type LocalControls } from './livekitLocalControls';
import { createLocalPublisher, type LocalPublisher } from './livekitPublisher';
import { createQualityTracker } from './livekitQuality';
import { createLiveKitRoom, ROOM_CONNECT_OPTIONS, resolveSfuUrl } from './livekitRoom';
import { HAND_ATTRIBUTE, rosterOf } from './livekitRoster';
import { applyPlan, EMPTY_PLAN, type PlanContext } from './livekitSubscriptions';
import { createMediaStore } from './mediaStore';
import {
  formatShareClaim, holdsPhantomClaim, mustYieldShare, resolveShareOwner,
  SHARE_CLAIM_ATTRIBUTE,
} from './shareArbiter';
import type { MediaRoomSeed } from './roomSeed';
import { watchVisibility } from './visibilityWatcher';

const STATS_TICK_MS = 2_000;
/**
 * Backoff for rejoining after a drop NOBODY ASKED FOR, and the number of tries
 * is the length of this list.
 *
 * livekit-client reconnects its own signal and transport, but some paths end the
 * room outright and never come back: the `freeze` listener it registers on every
 * web room calls `room.disconnect()` when Chrome freezes a backgrounded tab
 * (2.21 registers it unconditionally — `disconnectOnPageLeave: false` would not
 * turn it off), and its connection reconcile gives up after three mismatched
 * checks, which a throttled background tab reaches on timers alone. Both look
 * exactly like a hangup to everything downstream, which is how idling in another
 * window ended a call: "unpublishing track", "disconnect from room", gone.
 *
 * The call is not over because this tab stopped holding its seat, so the client
 * takes its seat back. The server side of the same fault is `reconcileWithSfu`
 * in apps/api, which no longer reads one empty seat as a departure.
 */
const REJOIN_BACKOFF_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000, 15_000];

export function createLiveKitMediaClient(seed: MediaRoomSeed): MediaClient {
  const store = createMediaStore(seed);
  const quality = createQualityTracker(store);
  const attachments = createAttachmentRegistry();
  const seedById = new Map(seed.participants.map((p) => [p.id, p]));
  const teardown: (() => void)[] = [];

  let room: Room | null = null;
  let publisher: LocalPublisher | null = null;
  let sink: AudioSink | null = null;
  let plan: SubscriptionPlan = EMPTY_PLAN;
  let statsTimer: number | null = null;
  let videoPaused = false;
  /** The options this session joined with, kept so a drop can be undone. */
  let joined: ConnectOptions | null = null;
  /** The user (or an unmounting call) asked to leave — stop taking the seat back. */
  let leaving = false;
  let rejoinAttempt = 0;
  let rejoinTimer: number | null = null;
  /**
   * Per-listener playback gain, kept OUTSIDE the room so it survives a
   * reconnect and a publisher re-subscribe. LiveKit forgets `setVolume` when a
   * track is republished, so the map is re-applied on every roster sync.
   */
  const volumes = new Map<string, number>();
  /** Re-entrancy guard: stopping a share re-enters `syncRoster`. */
  let stoppingShare = false;
  /** One in-flight withdrawal of a claim this client should not be holding. */
  let clearingClaim = false;
  /** One in-flight teardown of a publication this client cannot be feeding. */
  let clearingShare = false;

  /**
   * The freshest claimants, read from the ROOM rather than from the snapshot.
   *
   * The store deliberately drops writes that change nothing observable, so a
   * question asked between two syncs must go to the source. Before connect there
   * is no room and the seed roster is the only thing there is.
   */
  const claimants = (): readonly MediaParticipant[] =>
    room === null ? store.get().participants : rosterOf(room, seedById);

  /**
   * Publishes this client's stage claim as a participant attribute — the SFU
   * replicates it byte-for-byte, which is what lets every client resolve the
   * same presenter without asking apps/api anything.
   */
  const setShareClaim = async (claim: number | null): Promise<void> => {
    const participant = room?.localParticipant;
    if (!participant) return;
    await participant.setAttributes({
      ...participant.attributes,
      [SHARE_CLAIM_ATTRIBUTE]: formatShareClaim(claim),
    });
  };

  const applyVolumes = (): void => {
    if (!room) return;
    for (const [identity, volume] of volumes) {
      room.remoteParticipants.get(identity)?.setVolume(volume);
    }
  };

  /**
   * MY SHARE LOST — release the capture instead of uploading into a void.
   *
   * Without this, the loser of a simultaneous share went on encoding up to
   * ~1.6 Mbps of screen video that every viewer had explicitly UNSUBSCRIBED
   * (`livekitSubscriptions`), indefinitely, with the OS share indicator still
   * lit and nothing anywhere saying so. `stopScreenShare` is the same path the
   * Stop button takes, so the capture really is released and the indicator goes
   * out; the notice is left in the snapshot for the UI to say why.
   *
   * It lives in the PROVIDER and not in a React hook on purpose: it is the one
   * thing that must happen whether or not anything is mounted to notice.
   */
  const yieldShareIfLost = (
    roster: readonly MediaParticipant[],
    localId: string,
    ownerId: string | null,
  ): void => {
    if (stoppingShare || !mustYieldShare(roster, localId)) return;
    const winnerName = roster.find((p) => p.id === ownerId)?.name ?? 'Someone else';
    endLocalShare('taken-over', winnerName);
  };

  /**
   * A HOST STOPPED THIS SHARE — so stop it, all of it.
   *
   * `disable_share` mutes the publication and narrows `canPublishSources` at the
   * SFU. Neither of those can reach into this browser, and one LiveKit getter
   * decides how much of that this client even notices:
   *
   *     get isScreenShareEnabled() {           // RemoteParticipant
   *       const track = this.getTrackPublication(Track.Source.ScreenShare);
   *       return !!track;                      // MUTE-BLIND
   *     }
   *
   * — where `isCameraEnabled`, eight lines above it in the same class, is
   * `!track?.isMuted`. So `livekitRoster.sharing` is "a publication exists", and
   * a host's Stop moves it only if the SERVER removes the publication outright.
   *
   * Both ways it can land are wrong without this:
   *
   *   the publication SURVIVES → `sharing` stays true, `resolveShareOwner` keeps
   *     naming the stopped presenter, `PresenterBar` keeps telling them "You are
   *     presenting to everyone", the stage keeps showing a frozen frame, and
   *     every OTHER person's Share is refused with "X is presenting". A host
   *     Stop froze the stage instead of freeing it.
   *   the publication GOES → `sharing` goes false, so `mustYieldShare` returns
   *     false, so nothing calls `stopScreenShare`. `LocalVideoTrack.mute()`
   *     stops the underlying `MediaStreamTrack` for `Source.Camera` ONLY, so
   *     the display capture keeps running and the OS share indicator stays lit,
   *     with no button anywhere able to end it.
   *
   * The flag the host writes is the one thing that is true in both: it is
   * replicated on the roster like every other host decision, and it is read
   * here rather than in a React hook for the same reason the yield is — this
   * has to happen whether or not anything is mounted to notice.
   */
  const stopShareIfHostStopped = (
    roster: readonly MediaParticipant[],
    localId: string,
  ): void => {
    if (stoppingShare) return;
    const local = roster.find((p) => p.id === localId);
    if (local === undefined || !local.shareDisabled) return;
    /* Nothing left to stop: this is the steady state after the stop landed, and
       the flag stays set until a host presses "Allow them to share". */
    if (!local.sharing && !controls.shareCapturing) return;
    endLocalShare('host', null);
  };

  /**
   * WITHDRAW A CLAIM THIS CLIENT SHOULD NOT BE HOLDING.
   *
   * `shareArbiter.pendingPresenter` lets a claim with no track behind it block
   * the room, which is what stops the second presser stealing the stage from
   * somebody whose publish is still in flight. That is only safe while such a
   * claim is genuinely transient, and two things can strand one: `stopShare`'s
   * clear is best-effort and the room that refused the unpublish will refuse
   * it too, and a start can lose its publish after the claim has landed.
   *
   * Only this client can tell a stranded claim from a legitimate one — the
   * difference is whether its own start is still running — so only this client
   * cleans it up, on every roster sync, for itself.
   */
  const clearPhantomClaim = (roster: readonly MediaParticipant[], localId: string): void => {
    if (clearingClaim || stoppingShare) return;
    if (!holdsPhantomClaim(roster, localId, controls.shareCapturing, controls.shareStarting)) {
      return;
    }
    clearingClaim = true;
    void setShareClaim(null)
      .catch(() => undefined)
      .finally(() => {
        clearingClaim = false;
      });
  };

  /**
   * TAKE DOWN A SHARE THIS CLIENT CANNOT BE FEEDING.
   *
   * `livekitLocalControls` undoes a publish that landed after its share was
   * stopped, which is the fix at the source. This is the same rule stated where
   * nothing can race it: a publication of ours with no capture behind it and no
   * share verb in flight is rubbish — whatever put it there — and the roster
   * sync that notices takes it down. It is what makes that window SELF-HEAL
   * instead of needing a second Stop press, and it is the only thing that
   * repairs the case where the undo itself was refused by a reconnecting room.
   *
   * SILENT on purpose. The three sweeps above explain a share ended by somebody
   * ELSE — a host, or a winning claim. This one only ever runs after this client
   * stopped its own share, so the person already knows; a second sentence would
   * be a duplicate of the one they just caused. It goes through
   * `stopScreenShare` rather than `endLocalShare` for exactly that reason.
   */
  const clearOrphanedShare = (): void => {
    if (clearingShare || stoppingShare || !controls.shareOrphaned) return;
    clearingShare = true;
    void controls
      .stopScreenShare()
      .catch(() => false)
      .finally(() => {
        clearingShare = false;
      });
  };

  /**
   * STOP THIS CLIENT'S SHARE AND SAY SO — but only say so if it really stopped.
   *
   * `stopScreenShare` answers `false` when the capture is released and the wire
   * is not (a reconnecting room refuses `unpublishTrack`). Announcing on that
   * answer is how a bad network turned into a toast storm: `shareStopped.at` is
   * what the UI keys on, the guard was released in a `finally`, and every
   * roster-syncing room event — mute, unmute, attribute change, publish,
   * unpublish, join, leave — retried and patched a fresh timestamp. A rejection
   * is a reason to try again, never a reason to tell somebody their share was
   * stopped.
   */
  function endLocalShare(reason: ShareStopReason, winnerName: string | null): void {
    stoppingShare = true;
    void controls
      /* WHOSE stop this is, carried down so a start still publishing can say so
         instead of blaming the person for it - see `ShareStopCause`. */
      .stopScreenShare(reason)
      .catch(() => false)
      .then((clean) => {
        if (clean) store.patch({ shareStopped: { at: Date.now(), reason, winnerName } });
      })
      .finally(() => {
        stoppingShare = false;
        syncShareCapture();
      });
  }

  /** The OS truth into the snapshot, and only when it moved. */
  const syncShareCapture = (): void => {
    const capturing = controls.shareCapturing;
    if (capturing !== store.get().shareCapturing) store.patch({ shareCapturing: capturing });
  };

  const syncRoster = (): void => {
    if (!room) return;
    const roster = rosterOf(room, seedById);
    /* `shareArbiter`, never `roster.find`: array position is local-first and
       then this browser's own `remoteParticipants` order, so two viewers could
       resolve two different presenters and each presenter always resolved
       themselves. The arbiter reads replicated state through a total order. */
    const localId = room.localParticipant.identity;
    const ownerId = resolveShareOwner(roster);
    store.setRoster(roster, localId, ownerId);
    syncShareCapture();
    applyVolumes();
    /* A host's Stop first: it is the only one of the three that can be true at
       the same time as the other two, and it is the one that must win. */
    stopShareIfHostStopped(roster, localId);
    yieldShareIfLost(roster, localId, ownerId);
    clearPhantomClaim(roster, localId);
    /* Last, because the three above each have a SENTENCE to attach and this one
       is silent repair: if a host's Stop or a lost race is what left the
       publication behind, they get to explain it first. */
    clearOrphanedShare();
  };

  const context = (): PlanContext => ({
    profile: store.get().profile,
    activeSpeakerIds: store.get().activeSpeakerIds,
    screenShareOwnerId: store.get().screenShareOwnerId,
    videoPaused,
  });

  const reapplyPlan = (): void => {
    if (room) applyPlan(room, plan, context());
  };

  const controls: LocalControls = createLocalControls({
    getPublisher: () => publisher,
    getSink: () => sink,
    getProfile: () => store.get().profile,
    isConnected: () => room !== null,
    onPublicationsChanged: syncRoster,
    onProcessingChanged: () => store.patch({ processing: controls.media.processing }),
    patchLocal: (patch) => store.patchParticipant(store.get().localId, patch),
    onShareOwnerChanged: (owner) => {
      store.patch({ screenShareOwnerId: owner === 'local' ? store.get().localId : null });
      syncShareCapture();
    },
    /* THE OS TRUTH, ON THE BEAT IT CHANGES. Nothing else syncs the roster
       between a capture opening and its publish landing, so without this the
       snapshot said "no capture" for a whole signal round trip while the OS
       indicator was lit - and the Share button offered to start a SECOND one
       instead of stopping the first. */
    onShareCaptureChanged: syncShareCapture,
    getClaimants: claimants,
    getLocalId: () => room?.localParticipant.identity ?? store.get().localId,
    setShareClaim,
  });

  const stopSession = (): void => {
    if (statsTimer !== null) window.clearInterval(statsTimer);
    statsTimer = null;
    for (const off of teardown.splice(0)) off();
    attachments.clear();
    sink?.stop();
    sink = null;
    publisher = null;
  };

  /**
   * Join (or re-join) the room. CAPTURE IS NOT TOUCHED: the camera and
   * microphone this session already opened stay open across a rejoin, so taking
   * the seat back costs no `getUserMedia` round trip and no device permission
   * prompt — and the blur canvas the publisher is reading from survives.
   */
  const openRoom = async (options: ConnectOptions): Promise<void> => {
    /* Unbind before disconnecting, so the old room's own Disconnected event
       cannot be mistaken for a fresh drop and schedule another rejoin. */
    stopSession();
    const previous = room;
    room = null;
    if (previous !== null) await previous.disconnect().catch(() => undefined);

    const created = createLiveKitRoom(options.profile);
    room = created;
    sink = createAudioSink(created);
    publisher = createLocalPublisher(created, controls.media, () => store.get().profile);
    teardown.push(bindRoomEvents(created, host), watchAudioPlayback(created));

    await created.connect(resolveSfuUrl(options.url), options.token, ROOM_CONNECT_OPTIONS);
    await controls.syncPublications();
    // The sink only exists now, so a speaker chosen in pre-join is applied here.
    await controls.restoreOutputDevice();

    teardown.push(
      watchVisibility((hidden) => {
        videoPaused = hidden;
        reapplyPlan();
      }),
    );
    statsTimer = window.setInterval(() => void quality.tick(created), STATS_TICK_MS);
    const recording: RecordingState = created.isRecording ? 'recording' : store.get().recording;
    store.patch({ state: 'connected', recording, processing: controls.media.processing });
    syncRoster();
    reapplyPlan();
  };

  /**
   * LEAVE THE ROOM AND GIVE THE DEVICES BACK.
   *
   * The whole of `disconnect()`, extracted so the OTHER way out of a room can
   * use it: being removed. `controls.media.stop()` is the line that matters —
   * it calls `track.stop()` on the real capture tracks, which is what turns the
   * microphone and the camera light off.
   *
   * `endState` has no default on purpose. Those two ways out are not the same
   * event — one is this client leaving, the other is the server taking its seat
   * away — so each caller says which it is rather than inheriting an answer
   * from whoever wrote the signature.
   */
  const leaveRoom = async (endState: 'disconnected' | 'failed'): Promise<void> => {
    /* Set BEFORE anything tears down: this is the one signal that separates
       "we are leaving" from "we were dropped", and every rejoin path reads it. */
    leaving = true;
    joined = null;
    if (rejoinTimer !== null) window.clearTimeout(rejoinTimer);
    rejoinTimer = null;
    stopSession();
    const current = room;
    room = null;
    /*
     * THE DEVICES GO BACK FIRST, BEFORE THE AWAIT — NOT AFTER IT.
     *
     * `controls.media.stop()` is the line that calls `track.stop()` on the real
     * capture tracks; it is what turns the microphone light off. It used to sit
     * BEHIND `await room.disconnect()`, which is a network operation: livekit-
     * client waits for a leave acknowledgement, and on a dead network that
     * promise does not reject — it simply never settles. The end screen renders
     * and the microphone keeps publishing, for ever.
     *
     * On most paths a render rescues it: React unmounts `<CallRoom>`, the
     * second `leaveRoom()` finds `room === null`, `await undefined?.disconnect()`
     * resolves and the stop runs. It is NOT rescued on the one path built as
     * the last resort — an eviction (`onDropped` below) sets no `endedReason`,
     * causes no unmount, and fires precisely when the socket and REST are both
     * dead, which is the exact scenario `closeCallRoom` exists to cover.
     *
     * The leave signal is best-effort and always was (`.catch(() => undefined)`)
     * and the SFU times a vanished participant out regardless. The microphone
     * is not best-effort. So it goes first, and the ordering stops being an
     * accident of where a line happens to sit.
     */
    controls.media.stop();
    await current?.disconnect().catch(() => undefined);
    store.patch({
      state: endState, activeSpeakerIds: [], downlinkBps: 0, packetLoss: 0,
      processing: controls.media.processing, shareCapturing: false,
    });
  };

  /** Take the seat back after a drop the user did not ask for. */
  const rejoin = (): void => {
    if (leaving || joined === null || rejoinTimer !== null) return;
    const delay = REJOIN_BACKOFF_MS[rejoinAttempt];
    if (delay === undefined) {
      /* Out of tries. `failed` — not `disconnected` — because the UI's re-join
         affordance hangs off it, and the CALL may well still be live. */
      store.patch({ state: 'failed' });
      return;
    }
    rejoinAttempt += 1;
    store.patch({ state: 'reconnecting' });
    rejoinTimer = window.setTimeout(() => {
      rejoinTimer = null;
      const options = joined;
      if (leaving || options === null) return;
      void openRoom(options)
        .then(() => {
          rejoinAttempt = 0;
        })
        .catch(() => rejoin());
    }, delay);
  };

  const host = createRoomEventHost({
    store,
    attachments,
    getSink: () => sink,
    syncRoster,
    reapplyPlan,
    setLocalQuality: quality.setSignalled,
    /*
     * BEING REMOVED IS NOT A DROP, AND MUST NOT BE UNDONE.
     *
     * `rejoin` exists because livekit-client ends the room for reasons that
     * have nothing to do with leaving — Chrome's `freeze` on a backgrounded
     * tab calls `room.disconnect()` outright — and a call is not over because
     * a tab stopped holding its seat. But PARTICIPANT_REMOVED and ROOM_DELETED
     * come from the SERVER: a moderator's Remove, "end meeting for everyone",
     * or the API deleting the room of a call that has ended. LiveKit re-creates
     * a room on the next join, so rejoining an ENDED call put the client back
     * into a room of its own, alone, still publishing — which is exactly the
     * microphone this whole change exists to close. Leave, and stay left.
     */
    onDropped: (removedByServer) => {
      if (removedByServer) {
        /*
         * `failed`, NOT `disconnected`. Both mean "out of the room" to
         * `callMediaRegistry`, so the reconciliation ask fires either way — but
         * they say different things to a human. `disconnected` is what a
         * deliberate leave looks like, and an eviction is not one: this client
         * did not ask, and the CALL may well still be live (a LiveKit restart,
         * an operator `delete-room`, an `emptyTimeout` race). Reporting "you
         * left" for that leaves somebody in a silent call with nothing on
         * screen to say anything happened at all.
         */
        void leaveRoom('failed');
        return;
      }
      rejoin();
    },
  });

  return {
    subscribe: store.subscribe,
    getSnapshot: store.get,

    async connect(options: ConnectOptions) {
      leaving = false;
      rejoinAttempt = 0;
      store.patch({ state: 'connecting', profile: options.profile });
      controls.setWanted(options.cameraEnabled, options.micEnabled);
      try {
        // Processing preferences are applied BEFORE capture so the very first
        // published frame already carries the effect the user chose in pre-join.
        await controls.media.setAudioProcessing(options.audioProcessing);
        await controls.media.setVideoEffect(options.videoEffect);
        if (options.micEnabled) {
          await controls.media.setMic(true, options.micDeviceId).catch(() => null);
        }
        if (options.cameraEnabled) {
          await controls.media.setCamera(true, options.cameraDeviceId).catch(() => null);
        }
        if (options.speakerDeviceId) {
          await controls.setDevice('audiooutput', options.speakerDeviceId);
        }

        await openRoom(options);
        /* Only a session that actually reached the room may be resumed. */
        joined = options;
      } catch {
        // Never rethrow: the caller starts this without a handler and reads
        // `state` to decide whether to offer a re-join. Capture is released so
        // a failed join cannot leave the camera light on.
        stopSession();
        const failed = room;
        room = null;
        await failed?.disconnect().catch(() => undefined);
        controls.media.stop();
        store.patch({ state: 'failed' });
      }
    },

    /* Wrapped rather than handed over: `leaveRoom` now takes the state it
       should land in, and `MediaClient.disconnect()` takes no arguments —
       passing it straight through would let any caller decide how a deliberate
       leave is reported, and a react cleanup would pass its own first arg. */
    disconnect: () => leaveRoom('disconnected'),

    setMicEnabled: controls.setMicEnabled,
    setCameraEnabled: controls.setCameraEnabled,
    setDevice: controls.setDevice,
    setVideoEffect: controls.setVideoEffect,
    setAudioProcessing: controls.setAudioProcessing,
    startScreenShare: controls.startScreenShare,
    /* `LocalControls.stopScreenShare` answers whether the wire was really
       cleared, which is the provider's business and not the caller's: a Stop
       button has nothing to do differently either way, and the capture is gone
       in both. */
    async stopScreenShare() {
      await controls.stopScreenShare();
    },

    setProfile(profile: QualityProfile) {
      if (profile === store.get().profile) return;
      store.patch({ profile });
      reapplyPlan();
      void controls.syncPublications();
    },

    applySubscriptions(next) {
      plan = next;
      reapplyPlan();
    },

    attachVideo(participantId, source: VideoSource, el) {
      if (participantId === store.get().localId) {
        const media = source === 'screen' ? controls.media.screenStream : controls.media.cameraStream;
        if (!media) return () => undefined;
        el.srcObject = media;
        void el.play().catch(() => undefined);
        return () => {
          if (el.srcObject === media) el.srcObject = null;
        };
      }
      return attachments.request(room, participantId, source, el);
    },

    getLocalMicTrack() {
      // The RAW capture, deliberately: voice-activity detection must hear the
      // signal RNNoise is about to gate, otherwise the level meter goes dead in
      // exactly the quiet-room case it exists to visualise.
      return controls.media.rawMicStream?.getAudioTracks()[0] ?? null;
    },

    async setMicTransmitting(on) {
      const track = room?.localParticipant.getTrackPublication(Track.Source.Microphone)?.track;
      if (!track) return;
      // `mute()` also raises TrackMuted on every remote, so their mic glyph
      // follows push-to-talk without a single application-level message.
      await (on ? track.unmute() : track.mute()).catch(() => undefined);
      syncRoster();
    },

    setParticipantVolume(participantId, volume) {
      const clamped = Math.max(0, Math.min(2, volume));
      volumes.set(participantId, clamped);
      room?.remoteParticipants.get(participantId)?.setVolume(clamped);
    },

    setHandRaised(on) {
      const raisedAt = on ? Date.now() : null;
      store.patchParticipant(store.get().localId, { handRaisedAt: raisedAt });
      const participant = room?.localParticipant;
      if (!participant) return;
      void participant
        .setAttributes({ ...participant.attributes, [HAND_ATTRIBUTE]: on ? String(raisedAt) : '' })
        .catch(() => undefined);
    },

    sendReaction() {
      /* Reactions ride the gateway, not the SFU (ARCH §5) — no media effect. */
    },

    /* The four host controls below are SERVER authority (RoomService, called by
     * apps/api). A browser cannot mute or evict a peer, so these apply the
     * optimistic patch the UI needs; the next room event is what confirms it. */
    muteParticipant(participantId) {
      store.patchParticipant(participantId, { micOn: false, forceMuted: true, speaking: false });
    },

    removeParticipant(participantId) {
      store.setParticipants(store.get().participants.filter((p) => p.id !== participantId));
    },

    setSpotlight(participantId, on) {
      store.setParticipants(
        store.get().participants.map((p) =>
          p.id === participantId ? { ...p, spotlighted: on } : { ...p, spotlighted: false },
        ),
      );
    },

    setRecording(on) {
      store.patch({ recording: on ? 'starting' : 'stopping' });
    },
  };
}
